import { z } from 'zod';

/**
 * SCENE VALIDATION — is the scene split the parser read the screenplay's real
 * scene structure? (owner brief, 2026-10-29)
 *
 * It is the shared foundation of everything after it. The location analysis and
 * the detailed breakdown both read scenes, so a missed heading or two scenes run
 * together is wrong in both. Checking the split is a STRUCTURAL task: it says
 * nothing about locations, cast or props, and the screen that does it shows none.
 *
 * FOUR STATES, and a parsed scene starts in neither of the two a person decides:
 *
 * - `TO_REVIEW`: the parser found it and nobody has said yes yet.
 * - `NEEDS_ATTENTION`: the same, with something that looks wrong — no text to
 *   read, a heading the parser could not classify, a line inside it that reads
 *   like a missed heading, a scene number used twice.
 * - `CONFIRMED`: a person said "this is a scene, and its heading is right". A
 *   confirmation is never inferred from the parse; a bulk confirm is still a
 *   person's act, over the scenes they selected.
 * - `EXCLUDED`: a person said it is not a scene, or merged it into the scene
 *   before it. Nothing is deleted (ADR 0011): it leaves the active scene set and
 *   can be restored with its source reference.
 */
export const SceneValidationStatus = z.enum(['TO_REVIEW', 'NEEDS_ATTENTION', 'CONFIRMED', 'EXCLUDED']);
export type SceneValidationStatus = z.infer<typeof SceneValidationStatus>;

/**
 * Why a scene needs attention. Coded, so the screen and a later report agree on
 * what was flagged; each has one sentence on the screen and one remedy.
 *
 * - `NO_TEXT`: nothing to read under the heading. On a scene a person added it
 *   means the text was never typed; the AI breakdown has only the heading to go on.
 * - `HEADING_ONLY`: a parsed scene whose text is the heading line alone — an
 *   establishing shot, or a heading the parser took from a transition.
 * - `HEADING_NOT_RECOGNISED`: no INT./EXT. could be read from the heading.
 * - `POSSIBLE_MISSED_HEADING`: a line inside the text reads like a scene heading
 *   ("KITCHEN - CONTINUOUS"), so two scenes may be run together. Split is the remedy.
 * - `DUPLICATE_SCENE_NUMBER`: another active scene carries the same number.
 */
export const SceneValidationWarning = z.enum([
  'NO_TEXT',
  'HEADING_ONLY',
  'HEADING_NOT_RECOGNISED',
  'POSSIBLE_MISSED_HEADING',
  'DUPLICATE_SCENE_NUMBER',
]);
export type SceneValidationWarning = z.infer<typeof SceneValidationWarning>;

/** How a scene left the active set. A merged scene names the scene that absorbed it. */
export const SceneExclusionReason = z.enum(['NOT_A_SCENE', 'MERGED']);
export type SceneExclusionReason = z.infer<typeof SceneExclusionReason>;

/**
 * Whether the AI breakdown's reading of a scene is still about the scene as it
 * is now. `OUT_OF_DATE` is a scene changed (heading, text, split, merge) after
 * the AI read it: its proposals are kept, never erased, but they are not
 * presented as current.
 */
export const SceneAnalysisState = z.enum(['NOT_ANALYSED', 'CURRENT', 'OUT_OF_DATE']);
export type SceneAnalysisState = z.infer<typeof SceneAnalysisState>;

/** Where a workflow stands, from the rows it writes — never a shared flag. */
export const WorkflowStatus = z.enum(['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED']);
export type WorkflowStatus = z.infer<typeof WorkflowStatus>;

/**
 * The most text a person may type for a scene they added. A parsed scene's text
 * is capped at 2,000 characters when it is stored; a typed one is the producer's
 * whole description, so it gets more room, and stays well inside one context
 * block of the AI request (20,000 characters).
 */
export const MAX_SCENE_TEXT_CHARS = 10_000;

/** A slugline is stored in 512 characters; the heading form refuses more. */
export const MAX_SLUGLINE_CHARS = 512;

export const SceneIntExt = z.enum(['INT', 'EXT', 'INT_EXT']);
export type SceneIntExt = z.infer<typeof SceneIntExt>;

/**
 * The one rule from facts to a status. Excluded wins over everything; a person's
 * confirmation wins over a warning (they looked and said yes); otherwise a
 * warning makes it need attention.
 */
export function sceneValidationStatus(input: {
  excluded: boolean;
  confirmed: boolean;
  warnings: readonly SceneValidationWarning[];
}): SceneValidationStatus {
  if (input.excluded) return 'EXCLUDED';
  if (input.confirmed) return 'CONFIRMED';
  if (input.warnings.length > 0) return 'NEEDS_ATTENTION';

  return 'TO_REVIEW';
}

/**
 * Whether the AI's reading of one scene is current.
 *
 * `analysedAt` is the per-scene record the AI breakdown writes. Scenes read
 * before that record existed have none, so `versionAnalysedAt` — when the last
 * AI breakdown of the whole version started — stands in for a scene that already
 * existed then. A scene created after it was never read.
 *
 * A change at the same instant as the reading counts as read: the breakdown
 * stamps the moment it STARTED reading, so a later change is strictly later.
 */
export function sceneAnalysisState(input: {
  analysedAt: Date | null;
  versionAnalysedAt: Date | null;
  createdAt: Date | null;
  contentChangedAt: Date | null;
}): SceneAnalysisState {
  const readAt =
    input.analysedAt ??
    (input.versionAnalysedAt !== null &&
    (input.createdAt === null || input.createdAt.getTime() <= input.versionAnalysedAt.getTime())
      ? input.versionAnalysedAt
      : null);

  if (readAt === null) return 'NOT_ANALYSED';
  if (input.contentChangedAt !== null && input.contentChangedAt.getTime() > readAt.getTime()) return 'OUT_OF_DATE';

  return 'CURRENT';
}
